import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { clipTopicName } from '../../utils/topicUtils';
import { getCategoryPalette } from '../../utils/colorConstants';
import './UmapPanel.css';

/**
 * UmapPanel – 2-D UMAP scatter plot coloured by Topic.
 *
 * Props:
 *   data        – { points: [{x,y,proposalId,industry,topic,title}], industries: string[], topics: string[] }
 *   themeColors – from colorConstants.js
 *   theme       – 'autumn' | 'dark'
 */

function UmapPanel({ data, themeColors, theme, onPointClick, labels }) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 400 });
  const [hoveredPoint, setHoveredPoint] = useState(null);
  // Pan/zoom transform applied AFTER the base data→canvas mapping.
  // view.scale = zoom factor, view.tx/ty = pixel offset.
  const [view, setView] = useState({ scale: 1, tx: 0, ty: 0 });
  const dragRef = useRef({ active: false, startX: 0, startY: 0, baseTx: 0, baseTy: 0, moved: false });

  const isDark = theme === 'dark';

  // Reset zoom when underlying data changes (filter / view slice).
  useEffect(() => {
    setView({ scale: 1, tx: 0, ty: 0 });
  }, [data]);

  // ── Colour map (always by topic — uses topic palette from brand) ──────────
  const { colorMap, categories } = useMemo(() => {
    if (!data) return { colorMap: {}, categories: [] };
    const palette = getCategoryPalette('topic', theme);
    const cats = data.topics || [];
    const map = {};
    cats.forEach((cat, i) => {
      map[cat] = palette[i % palette.length];
    });
    return { colorMap: map, categories: cats };
  }, [data, theme]);

  // ── Coordinate transform helpers ──────────────────────────────────────────
  const layout = useMemo(() => {
    if (!data || !data.points || data.points.length === 0) return null;

    // Reserve space for legend on the right (proportional to canvas width)
    const canvasW = dimensions.width;
    const canvasH = dimensions.height;
    const legendColW = Math.max(140, Math.min(320, Math.floor(canvasW * 0.38)));
    const padding = { top: 44, right: legendColW + 16, bottom: 30, left: 40 };

    const plotW = canvasW - padding.left - padding.right;
    const plotH = canvasH - padding.top - padding.bottom;

    const xs = data.points.map(p => p.x);
    const ys = data.points.map(p => p.y);
    const xMin = Math.min(...xs), xMax = Math.max(...xs);
    const yMin = Math.min(...ys), yMax = Math.max(...ys);
    const xRange = xMax - xMin || 1;
    const yRange = yMax - yMin || 1;
    // Add 5% margin to data range
    const xPad = xRange * 0.05;
    const yPad = yRange * 0.05;

    const toCanvasX = (v) => padding.left + ((v - (xMin - xPad)) / (xRange + 2 * xPad)) * plotW;
    const toCanvasY = (v) => padding.top + plotH - ((v - (yMin - yPad)) / (yRange + 2 * yPad)) * plotH; // invert y

    return { padding, plotW, plotH, legendColW, toCanvasX, toCanvasY, canvasW, canvasH };
  }, [data, dimensions, categories]);

  // Projection composing pan+zoom on top of the base layout mapping. Zoom is
  // applied around the plot centre, then translated by tx/ty.
  const project = useMemo(() => {
    if (!layout) return null;
    const { padding, plotW, plotH, toCanvasX, toCanvasY } = layout;
    const cx0 = padding.left + plotW / 2;
    const cy0 = padding.top + plotH / 2;
    const apply = (x, y) => {
      const bx = toCanvasX(x);
      const by = toCanvasY(y);
      return [cx0 + (bx - cx0) * view.scale + view.tx,
              cy0 + (by - cy0) * view.scale + view.ty];
    };
    return { apply, cx0, cy0 };
  }, [layout, view]);

  // ── Resize observer ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height
        });
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // ── Draw ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!canvasRef.current || !data || !layout || !project) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    const cw = layout.canvasW;
    const ch = layout.canvasH;
    canvas.width = cw * dpr;
    canvas.height = ch * dpr;
    canvas.style.width = `${cw}px`;
    canvas.style.height = `${ch}px`;
    ctx.scale(dpr, dpr);

    const textColor = isDark ? '#ddd' : '#444';
    const gridColor = isDark ? '#333' : '#e8e8e8';
    const { padding, plotW, plotH, legendColW } = layout;

    ctx.clearRect(0, 0, cw, ch);

    // Title
    ctx.font = 'bold 14px "DIN 2014 ROUNDED REGULAR", "Segoe UI", sans-serif';
    ctx.fillStyle = isDark ? '#eee' : '#333';
    ctx.textAlign = 'center';
    ctx.fillText('UMAP Projection — coloured by Topic  ·  scroll to zoom, drag to pan, double-click to reset', cw / 2, 22);

    // Plot area background
    ctx.fillStyle = isDark ? 'rgba(30,30,30,0.5)' : 'rgba(250,250,250,0.6)';
    ctx.fillRect(padding.left, padding.top, plotW, plotH);
    ctx.strokeStyle = gridColor;
    ctx.lineWidth = 1;
    ctx.strokeRect(padding.left, padding.top, plotW, plotH);

    // Clip subsequent point/label drawing to the plot rectangle so zoomed-out
    // points don't bleed into legend/axis areas.
    ctx.save();
    ctx.beginPath();
    ctx.rect(padding.left, padding.top, plotW, plotH);
    ctx.clip();

    // Points — density-aware rendering scaled by current zoom.
    const n = data.points.length;
    const dense = n > 2000;
    const baseRadius = dense ? (n > 20000 ? 1.6 : n > 8000 ? 2.0 : 2.6)
                             : (n > 400 ? 3.5 : n > 150 ? 4.5 : 5.5);
    const radius = baseRadius * Math.min(2.5, Math.max(0.6, Math.sqrt(view.scale)));
    const alpha  = dense ? (n > 20000 ? 0.12 : n > 8000 ? 0.18 : 0.28) : 0.78;
    const useStroke = !dense;

    ctx.globalAlpha = alpha;
    for (const pt of data.points) {
      const [cx, cy] = project.apply(pt.x, pt.y);
      ctx.fillStyle = colorMap[pt.topic] || '#999';
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.fill();
      if (useStroke) {
        ctx.strokeStyle = isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.15)';
        ctx.lineWidth = 0.6;
        ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;

    // Cluster centroid labels — compute mean (x,y) per topic and label the
    // largest clusters so dense areas have visible identity. Skipped when
    // there are no topics or when the dataset is tiny.
    if (n > 200 && categories.length > 0) {
      const acc = {}; // topic -> {sx, sy, count}
      for (const pt of data.points) {
        const t = pt.topic;
        if (!t) continue;
        if (!acc[t]) acc[t] = { sx: 0, sy: 0, count: 0 };
        acc[t].sx += pt.x;
        acc[t].sy += pt.y;
        acc[t].count += 1;
      }
      // Label all topics with enough points to matter (>=0.5% of total), so
      // every visible cluster gets named — not just the 12 biggest.
      const minCount = Math.max(3, Math.floor(n * 0.005));
      const ranked = Object.entries(acc)
        .filter(([, agg]) => agg.count >= minCount)
        .sort((a, b) => b[1].count - a[1].count);
      ctx.font = 'bold 11px "Segoe UI", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      for (const [topic, agg] of ranked) {
        const [cx, cy] = project.apply(agg.sx / agg.count, agg.sy / agg.count);
        const label = clipTopicName(topic);
        const w = ctx.measureText(label).width;
        // Halo box for readability
        ctx.fillStyle = isDark ? 'rgba(20,20,20,0.78)' : 'rgba(255,255,255,0.85)';
        ctx.fillRect(cx - w / 2 - 4, cy - 8, w + 8, 16);
        ctx.fillStyle = colorMap[topic] || (isDark ? '#fff' : '#222');
        ctx.fillText(label, cx, cy);
      }
    }
    ctx.restore(); // end clip

    // Axis labels
    ctx.font = '10px "Segoe UI", sans-serif';
    ctx.fillStyle = textColor;
    ctx.globalAlpha = 0.6;
    ctx.textAlign = 'center';
    ctx.fillText('UMAP-1', padding.left + plotW / 2, padding.top + plotH + 18);
    ctx.save();
    ctx.translate(14, padding.top + plotH / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('UMAP-2', 0, 0);
    ctx.restore();
    ctx.globalAlpha = 1;

    // Legend (right column — dynamic font size, no truncation)
    const legendLeft = padding.left + plotW + 16;
    const legendAvailH = plotH;
    const legendItemH = Math.max(12, Math.min(20, Math.floor(legendAvailH / (categories.length || 1))));
    // Scale font to fit: size proportional to column width, clamped
    const legendFontSize = Math.max(8, Math.min(13, Math.floor(legendColW / 12)));
    const legendStartY = padding.top + 4;
    ctx.font = `${legendFontSize}px "Segoe UI", sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';

    categories.forEach((cat, i) => {
      const ly = legendStartY + i * legendItemH;
      if (ly + legendItemH > ch) return; // clip if too many

      // Swatch
      const swatchR = Math.max(2.5, Math.min(4.5, legendFontSize * 0.38));
      ctx.fillStyle = colorMap[cat] || '#999';
      ctx.beginPath();
      ctx.arc(legendLeft + swatchR, ly, swatchR, 0, Math.PI * 2);
      ctx.fill();

      // Label (full name, no truncation)
      ctx.fillStyle = textColor;
      const label = clipTopicName(cat);
      ctx.fillText(label, legendLeft + swatchR * 2 + 6, ly + 1);
    });

  }, [data, layout, project, dimensions, isDark, colorMap, categories, view]);

  // ── Mouse-move tooltip ────────────────────────────────────────────────────
  const handleMouseMove = useCallback((e) => {
    if (!layout || !data || !project || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    const baseRadius = data.points.length > 400 ? 3.5 : data.points.length > 150 ? 4.5 : 5.5;
    const hitRadius = baseRadius * Math.min(2.5, Math.max(0.6, Math.sqrt(view.scale))) + 4;

    let closest = null;
    let closestDist = Infinity;
    for (const pt of data.points) {
      const [cx, cy] = project.apply(pt.x, pt.y);
      const d = Math.hypot(mx - cx, my - cy);
      if (d < hitRadius && d < closestDist) {
        closestDist = d;
        closest = pt;
      }
    }

    if (closest) {
      setHoveredPoint({ ...closest, screenX: e.clientX, screenY: e.clientY });
    } else {
      setHoveredPoint(null);
    }
  }, [layout, project, data, view]);

  const handleMouseLeave = useCallback(() => setHoveredPoint(null), []);

  // Click on the canvas: hit-test against points and fire onPointClick.
  const handleClick = useCallback((e) => {
    if (!onPointClick || !layout || !data || !project || !canvasRef.current) return;
    if (dragRef.current.moved) return; // suppress click after a pan
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const baseRadius = data.points.length > 400 ? 3.5 : data.points.length > 150 ? 4.5 : 5.5;
    const hitRadius = baseRadius * Math.min(2.5, Math.max(0.6, Math.sqrt(view.scale))) + 4;
    let closest = null;
    let closestDist = Infinity;
    for (const pt of data.points) {
      const [cx, cy] = project.apply(pt.x, pt.y);
      const d = Math.hypot(mx - cx, my - cy);
      if (d < hitRadius && d < closestDist) {
        closestDist = d;
        closest = pt;
      }
    }
    if (closest) onPointClick(closest, e);
  }, [onPointClick, layout, project, data, view]);

  // ── Wheel zoom (non-passive so we can preventDefault) ─────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !layout) return;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const factor = Math.exp(-e.deltaY * 0.0015); // smooth multiplicative zoom
      setView(v => {
        const newScale = Math.min(40, Math.max(0.5, v.scale * factor));
        const k = newScale / v.scale;
        const { padding, plotW, plotH } = layout;
        const cx0 = padding.left + plotW / 2;
        const cy0 = padding.top + plotH / 2;
        // Keep the point under the cursor stationary.
        // After old: px = cx0 + (bx-cx0)*v.scale + v.tx
        // After new: px = cx0 + (bx-cx0)*newScale + newTx  → newTx = px - cx0 - (bx-cx0)*newScale
        // (bx-cx0) = (px - cx0 - v.tx) / v.scale  ⇒  newTx = mx - cx0 - k*(mx - cx0 - v.tx)
        const newTx = mx - cx0 - k * (mx - cx0 - v.tx);
        const newTy = my - cy0 - k * (my - cy0 - v.ty);
        return { scale: newScale, tx: newTx, ty: newTy };
      });
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [layout]);

  // ── Drag to pan ───────────────────────────────────────────────────────────
  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    dragRef.current = {
      active: true, moved: false,
      startX: e.clientX, startY: e.clientY,
      baseTx: view.tx, baseTy: view.ty,
    };
  }, [view]);

  const handleMouseMoveCombined = useCallback((e) => {
    if (dragRef.current.active) {
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      if (!dragRef.current.moved && Math.hypot(dx, dy) > 3) {
        dragRef.current.moved = true;
      }
      if (dragRef.current.moved) {
        setView(v => ({ ...v, tx: dragRef.current.baseTx + dx, ty: dragRef.current.baseTy + dy }));
        return;
      }
    }
    handleMouseMove(e);
  }, [handleMouseMove]);

  const handleMouseUp = useCallback(() => {
    dragRef.current.active = false;
    // keep .moved flag until next click cycle so handleClick can suppress
    setTimeout(() => { dragRef.current.moved = false; }, 0);
  }, []);

  const handleDoubleClick = useCallback(() => {
    setView({ scale: 1, tx: 0, ty: 0 });
  }, []);

  // ── Empty state ───────────────────────────────────────────────────────────
  if (!data || !data.points || data.points.length === 0) {
    return (
      <div className="umap-panel umap-empty" ref={containerRef}>
        <p>No UMAP data available for the current filters.</p>
        <p className="umap-empty-hint">
          UMAP coordinates are sourced from the master data file.
        </p>
      </div>
    );
  }

  return (
    <div className="umap-panel" ref={containerRef}>
      <canvas
        ref={canvasRef}
        className="umap-canvas"
        style={{ cursor: dragRef.current.active ? 'grabbing' : (onPointClick ? 'pointer' : 'grab') }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMoveCombined}
        onMouseUp={handleMouseUp}
        onMouseLeave={(e) => { handleMouseUp(); handleMouseLeave(e); }}
        onDoubleClick={handleDoubleClick}
        onClick={handleClick}
      />
      {(view.scale !== 1 || view.tx !== 0 || view.ty !== 0) && (
        <button
          type="button"
          className="umap-reset-btn"
          onClick={() => setView({ scale: 1, tx: 0, ty: 0 })}
          style={{
            position: 'absolute', top: 8, right: 8, zIndex: 5,
            padding: '4px 10px', fontSize: '0.75rem',
            background: isDark ? 'rgba(40,40,40,0.85)' : 'rgba(255,255,255,0.9)',
            color: isDark ? '#eee' : '#333',
            border: `1px solid ${isDark ? '#555' : '#ccc'}`,
            borderRadius: 4, cursor: 'pointer',
          }}
        >
          Reset view ({view.scale.toFixed(1)}×)
        </button>
      )}
      {data.sampled && data.totalPoints > data.points.length && (
        <div
          className="umap-sampled-badge"
          title={`Each topic is capped at its 60 paragraphs closest to the topic centroid. With ${(data.topics || []).length} topics in view that yields ${data.points.length.toLocaleString()} of ${data.totalPoints.toLocaleString()} total. Narrow with filters to see every point.`}
          style={{
            position: 'absolute', bottom: 8, left: 8, zIndex: 5,
            padding: '4px 10px', fontSize: '0.7rem',
            background: isDark ? 'rgba(40,40,40,0.85)' : 'rgba(255,255,255,0.9)',
            color: isDark ? '#ccc' : '#555',
            border: `1px solid ${isDark ? '#555' : '#ccc'}`,
            borderRadius: 4, fontStyle: 'italic',
          }}
        >
          showing 60 closest paragraphs per topic ({data.points.length.toLocaleString()} of {data.totalPoints.toLocaleString()})
        </div>
      )}
      {onPointClick && (
        <div
          className="umap-click-hint"
          style={{
            position: 'absolute', bottom: 8, right: 8, zIndex: 5,
            padding: '4px 10px', fontSize: '0.7rem',
            background: isDark ? 'rgba(40,40,40,0.85)' : 'rgba(255,255,255,0.9)',
            color: isDark ? '#aaa' : '#666',
            border: `1px solid ${isDark ? '#555' : '#ccc'}`,
            borderRadius: 4,
            pointerEvents: 'none',
          }}
        >
          click: open paragraph · <strong>shift+click</strong>: docs on topic
        </div>
      )}
      {hoveredPoint && (
        <div
          className="umap-tooltip"
          style={{ left: hoveredPoint.screenX + 14, top: hoveredPoint.screenY - 50 }}
        >
          <strong>{hoveredPoint.proposalId}</strong><br />
          {labels?.industry || 'Industry'}: <strong>{hoveredPoint.industry}</strong><br />
          Topic: <strong>{hoveredPoint.topic}</strong>
          {hoveredPoint.title && (
            <><br /><em style={{ opacity: 0.8, fontSize: '0.74rem' }}>{hoveredPoint.title.substring(0, 80)}{hoveredPoint.title.length > 80 ? '…' : ''}</em></>
          )}
        </div>
      )}
    </div>
  );
}

export default UmapPanel;
